import { useEffect, useState } from "react";
import type React from "react";
import { api } from "../../api/client";
import { theme, btnPrimary, btnSecondary } from "../../theme";
import { useConsultantShell } from "./ConsultantShellContext";
import { ASSIGNMENT_ROLE_LABELS, errText, type Assignee, type AssignmentRole, type StaffMember } from "./workspace";

// ── Client summary strip ──────────────────────────────────────────────────
// The facts a consultant wants before anything else on a client, in one row:
// who leads it, the tier / package, consultancy hours booked this month against
// the allowance, the next statutory deadline, the next booked session and open
// findings. A firm administrator edits the assignments from here, and a senior
// consultant (org:admin) can send the client a reminder of what is waiting on them.

interface Slot { status: string; hours: number; slotDate: string }
interface Clock { label: string; dueAt: string; urgency: string }

export interface SummaryClient {
  id: string;
  billingMode: string;
  managedServiceDaysPerMonth: number;
  managedServiceHoursPerMonth: number;
  consultancyDaysPerMonth: number;
}

const DAY = 8;

export function ClientSummaryStrip({ client }: { client: SummaryClient }) {
  const { portfolio, isFirmAdmin, reloadPortfolio, permissions } = useConsultantShell();
  const summary = portfolio.orgs.find((o) => o.id === client.id);
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [clock, setClock] = useState<Clock | null>(null);
  const [editing, setEditing] = useState(false);
  const [reminding, setReminding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const month = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    const nextMonth = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}`;
    Promise.all([
      api.get<{ slots: Slot[] }>("/billing/slots", { month, organizationId: client.id }),
      api.get<{ slots: Slot[] }>("/billing/slots", { month: nextMonth, organizationId: client.id }),
    ])
      .then(([a, b]) => { if (!cancelled) setSlots([...a.slots, ...b.slots]); })
      .catch(() => { if (!cancelled) setSlots([]); });
    api.get<{ deadline?: Clock | null }>("/nav/counts", { organizationId: client.id })
      .then((d) => { if (!cancelled) setClock(d.deadline ?? null); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [client.id]);

  const assignees = summary?.assignees ?? [];
  const lead = assignees.find((a) => a.role === "lead");
  const thisMonth = new Date().toISOString().slice(0, 7);
  const bookedHours = (slots ?? [])
    .filter((s) => s.slotDate.slice(0, 7) === thisMonth && (s.status === "confirmed" || s.status === "pending"))
    .reduce((n, s) => n + s.hours, 0);
  const allowance = client.billingMode === "managed_service"
    ? client.managedServiceDaysPerMonth * DAY + client.managedServiceHoursPerMonth
    : client.consultancyDaysPerMonth * DAY;
  const today = new Date().toISOString().slice(0, 10);
  const nextSession = (slots ?? [])
    .filter((s) => s.status === "confirmed" && s.slotDate.slice(0, 10) >= today)
    .sort((a, b) => a.slotDate.localeCompare(b.slotDate))[0];
  const packageLabel = summary?.smallBusinessPackage ? "CE Annual Package" : summary?.bundleTier ? `${summary.bundleTier[0].toUpperCase()}${summary.bundleTier.slice(1)} bundle` : null;
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

  const cell = (label: string, value: React.ReactNode, tone?: string) => (
    <div style={{ minWidth: 130 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: tone ?? theme.color.text, marginTop: 2 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "flex-start" }}>
        {cell("Lead consultant", (
          <span>
            {lead ? lead.displayName : <span style={{ color: theme.color.faint }}>{assignees.length ? "No lead" : "Unassigned"}</span>}
            {assignees.length > (lead ? 1 : 0) && (
              <span style={{ fontSize: 11.5, fontWeight: 500, color: theme.color.subtle }}> +{assignees.length - (lead ? 1 : 0)}</span>
            )}
            {isFirmAdmin && (
              <button onClick={() => setEditing((e) => !e)} style={{ marginLeft: 8, background: "none", border: "none", padding: 0, fontSize: 11.5, color: theme.color.accentText, cursor: "pointer", textDecoration: "underline" }}>
                {editing ? "Close" : "Manage"}
              </button>
            )}
          </span>
        ))}
        {cell("Tier", summary ? `${summary.serviceTier.name}${packageLabel ? ` · ${packageLabel}` : ""}` : "—")}
        {cell("Hours this month", slots === null ? "…" : allowance > 0 ? `${bookedHours} of ${allowance} h` : `${bookedHours} h booked`,
          allowance > 0 && bookedHours > allowance ? theme.color.warningText : undefined)}
        {cell("Next deadline", clock ? <span title={clock.label}>{clock.label.length > 34 ? `${clock.label.slice(0, 32)}…` : clock.label} · {fmt(clock.dueAt)}</span> : "None due",
          clock?.urgency === "overdue" ? theme.color.dangerText : clock?.urgency === "due_soon" ? theme.color.warningText : undefined)}
        {cell("Next session", nextSession ? `${fmt(nextSession.slotDate)} · ${nextSession.hours} h` : "None booked")}
        {cell("Open findings", summary ? String(summary.openFindings) : "—", (summary?.openFindings ?? 0) > 0 ? theme.color.dangerText : undefined)}
      </div>
      {permissions.includes("org:admin") && !reminding && (
        <div><button onClick={() => setReminding(true)} style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12 }}>Send the client a reminder</button></div>
      )}
      {reminding && <ReminderForm orgId={client.id} onDone={() => setReminding(false)} />}
      {editing && <AssignmentEditor orgId={client.id} current={assignees} onSaved={() => { setEditing(false); reloadPortfolio(); }} />}
    </div>
  );
}

/** Emails the client's administrators what is waiting on them (POST /consultant/workspace/reminders). */
function ReminderForm({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const send = () => {
    setSending(true);
    api.post<{ results: Array<{ status: "sent" | "skipped"; reason?: string; recipients?: number }> }>(
      "/consultant/workspace/reminders", { organizationIds: [orgId], note: note.trim() || undefined })
      .then(({ results }) => {
        const r = results[0];
        setResult(r?.status === "sent" ? `Sent to ${r.recipients} administrator${r.recipients !== 1 ? "s" : ""}.` : `Not sent — ${r?.reason ?? "unknown reason"}.`);
      })
      .catch((e) => setResult(errText(e, "Could not send the reminder")))
      .finally(() => setSending(false));
  };
  return (
    <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, color: theme.color.subtle }}>The client's administrators get one email listing the actions waiting for them, with your note. One reminder per client per day.</div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} rows={2} placeholder="Optional note" aria-label="Reminder note"
        style={{ padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, fontFamily: theme.font, resize: "vertical" }} />
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {!result && <button onClick={send} disabled={sending} style={{ ...btnPrimary, padding: "5px 14px", fontSize: 12 }}>{sending ? "Sending…" : "Send reminder"}</button>}
        <button onClick={onDone} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12 }}>{result ? "Close" : "Cancel"}</button>
        {result && <span role="status" style={{ fontSize: 12.5, color: theme.color.text }}>{result}</span>}
      </div>
    </div>
  );
}

function AssignmentEditor({ orgId, current, onSaved }: { orgId: string; current: Assignee[]; onSaved: () => void }) {
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [rows, setRows] = useState<Array<{ userId: string; role: AssignmentRole }>>(current.map((a) => ({ userId: a.userId, role: a.role })));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ staff: StaffMember[] }>("/consultant/workspace/staff").then((d) => setStaff(d.staff)).catch((e) => setError(errText(e, "Could not load consultants")));
  }, []);

  const setRole = (userId: string, role: AssignmentRole) => setRows((rs) => rs.map((r) => {
    if (r.userId === userId) return { ...r, role };
    // One lead per client: choosing a new lead makes the old one supporting.
    return role === "lead" && r.role === "lead" ? { ...r, role: "supporting" } : r;
  }));
  const add = (userId: string) => { if (userId && !rows.some((r) => r.userId === userId)) setRows((rs) => [...rs, { userId, role: rs.some((r) => r.role === "lead") ? "supporting" : "lead" }]); };
  const remove = (userId: string) => setRows((rs) => rs.filter((r) => r.userId !== userId));

  const save = () => {
    setSaving(true);
    setError(null);
    api.put(`/consultant/workspace/clients/${orgId}/assignments`, { assignments: rows })
      .then(onSaved)
      .catch((e) => setError(errText(e, "Could not save the assignments")))
      .finally(() => setSaving(false));
  };

  const input: React.CSSProperties = { padding: "5px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5 };
  const name = (id: string) => staff.find((s) => s.id === id)?.displayName ?? current.find((c) => c.userId === id)?.displayName ?? "…";

  return (
    <div style={{ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 12, color: theme.color.subtle }}>Assignments decide whose "My clients" view, Today list and badge include this client. They never change what anyone can open.</div>
      {rows.map((r) => (
        <div key={r.userId} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ flex: 1, fontSize: 13, color: theme.color.text }}>{name(r.userId)}</span>
          <select value={r.role} onChange={(e) => setRole(r.userId, e.target.value as AssignmentRole)} aria-label={`Role for ${name(r.userId)}`} style={input}>
            {(Object.keys(ASSIGNMENT_ROLE_LABELS) as AssignmentRole[]).map((k) => <option key={k} value={k}>{ASSIGNMENT_ROLE_LABELS[k]}</option>)}
          </select>
          <button onClick={() => remove(r.userId)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 11.5 }}>Remove</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value="" onChange={(e) => add(e.target.value)} aria-label="Add a consultant" style={input}>
          <option value="">Add a consultant…</option>
          {staff.filter((s) => !rows.some((r) => r.userId === s.id)).map((s) => (
            <option key={s.id} value={s.id}>{s.displayName} — {s.assignedCount} client{s.assignedCount !== 1 ? "s" : ""}, {s.bookedHoursThisMonth} h booked</option>
          ))}
        </select>
        <button onClick={save} disabled={saving} style={{ ...btnPrimary, padding: "5px 14px", fontSize: 12, marginLeft: "auto" }}>{saving ? "Saving…" : "Save"}</button>
      </div>
      {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
